import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Button, 
  Input, 
  Space, 
  Tag, 
  Popconfirm, 
  message, 
  Row,
  Col,
  Alert,
  Tooltip,
  Tree,
  Empty,
  Spin,
  Switch
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined,
  BookOutlined,
  FolderOutlined,
  FileOutlined,
  UserOutlined,
  DragOutlined,
  ExpandOutlined,
  CompressOutlined
} from '@ant-design/icons';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { SpecialtyTree, SpecialtyFilters, specialtyAPI } from '../../services/specialtyAPI';
import { UserCompany, Company } from '../../services/companyAPI';
import SpecialtyForm from './SpecialtyForm';

const { Search } = Input;
const { DirectoryTree } = Tree;

// Union tip yaradırıq
type SelectedCompany = UserCompany | Company;

interface SpecialtiesListProps {
  selectedCompany: SelectedCompany | null;
}

interface TreeNode {
  title: React.ReactNode;
  key: string;
  icon?: React.ReactNode;
  children?: TreeNode[];
  isLeaf?: boolean;
  specialty: SpecialtyTree;
}

// Sortable Tree Node komponenti
interface SortableTreeNodeProps {
  specialty: SpecialtyTree;
  onEdit: (specialty: SpecialtyTree) => void;
  onDelete: (id: number, name: string) => void;
  onAddChild: (specialty: SpecialtyTree) => void;
}

const SortableTreeNode: React.FC<SortableTreeNodeProps> = ({ 
  specialty, 
  onEdit, 
  onDelete, 
  onAddChild 
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: specialty.id.toString() });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center justify-between p-3 mb-2 bg-white border rounded-lg shadow-sm hover:shadow-md transition-shadow ${
        isDragging ? 'shadow-lg' : ''
      }`}
    >
      <div className="flex items-center space-x-3">
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-100 rounded"
        >
          <DragOutlined className="text-gray-400" />
        </div>
        
        <div className="flex items-center space-x-2">
          {specialty.children && specialty.children.length > 0 ? (
            <FolderOutlined className="text-blue-500" />
          ) : (
            <FileOutlined className="text-green-500" />
          )}
          
          <span className="font-medium text-gray-900">{specialty.name}</span>
          
          <Tag color={specialty.is_active ? 'green' : 'red'}>
            {specialty.is_active ? 'Aktiv' : 'Deaktiv'}
          </Tag>
          
          {specialty.teacher_count > 0 && (
            <Tag color="blue">
              <UserOutlined className="mr-1" />
              {specialty.teacher_count}
            </Tag>
          )}
        </div>
      </div>

      <div className="flex items-center space-x-1">
        <Tooltip title="Alt ixtisas əlavə et">
          <Button
            type="text"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => onAddChild(specialty)}
          />
        </Tooltip>
        <Tooltip title="Redaktə et">
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => onEdit(specialty)}
          />
        </Tooltip>
        <Popconfirm
          title="İxtisası silmək"
          description={`${specialty.name} ixtisasını${specialty.children && specialty.children.length > 0 ? ' və bütün alt ixtisaslarını' : ''} silmək istədiyinizə əminsiniz?`}
          onConfirm={() => onDelete(specialty.id, specialty.name)}
          okText="Bəli"
          cancelText="Xeyr"
          okType="danger"
        >
          <Tooltip title="Sil">
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
            />
          </Tooltip>
        </Popconfirm>
      </div>
    </div>
  );
};

const SpecialtiesList: React.FC<SpecialtiesListProps> = ({ selectedCompany }) => {
  const [specialties, setSpecialties] = useState<SpecialtyTree[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingSpecialty, setEditingSpecialty] = useState<SpecialtyTree | null>(null);
  const [parentSpecialty, setParentSpecialty] = useState<SpecialtyTree | null>(null);
  const [filters, setFilters] = useState<SpecialtyFilters>({});
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [autoExpandParent, setAutoExpandParent] = useState(true);
  const [dragMode, setDragMode] = useState(true);
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (selectedCompany) {
      loadSpecialties();
    }
  }, [selectedCompany, filters]);

  const loadSpecialties = async () => {
    if (!selectedCompany) return;
    
    setLoading(true);
    try {
      const response = await specialtyAPI.getSpecialtyTree(selectedCompany.id);
      
      if (response.success && response.data) {
        // Data-nın array olduğunu təmin et
        const specialtyData = Array.isArray(response.data) ? response.data : [];
        setSpecialties(specialtyData);
        // İlk yükləmədə bütün node-ları aç
        if (expandedKeys.length === 0) {
          const allKeys = getAllKeys(specialtyData);
          setExpandedKeys(allKeys);
        }
      } else {
        message.error(response.message || 'İxtisaslar yüklənmədi');
        setSpecialties([]); // Xəta halında boş array
      }
    } catch (error) {
      console.error('İxtisaslar yüklənmədi:', error);
      message.error('İxtisaslar yüklənmədi');
      setSpecialties([]); // Xəta halında boş array
    } finally {
      setLoading(false);
    }
  };

  const getAllKeys = (data: SpecialtyTree[]): string[] => {
    if (!Array.isArray(data)) return [];
    
    const keys: string[] = [];
    const traverse = (nodes: SpecialtyTree[]) => {
      if (!Array.isArray(nodes)) return;
      
      nodes.forEach(node => {
        keys.push(node.id.toString());
        if (node.children && Array.isArray(node.children) && node.children.length > 0) {
          traverse(node.children);
        }
      });
    };
    traverse(data);
    return keys;
  };

  const buildTreeData = (data: SpecialtyTree[]): TreeNode[] => {
    if (!Array.isArray(data)) return [];
    
    return data.map(specialty => ({
      title: (
        <div className="flex items-center justify-between w-full group">
          <div className="flex items-center space-x-2">
            <span className="font-medium">{specialty.name}</span>
            <Tag color={specialty.is_active ? 'green' : 'red'}>
              {specialty.is_active ? 'Aktiv' : 'Deaktiv'}
            </Tag>
            {specialty.teacher_count > 0 && (
              <Tag color="blue">
                <UserOutlined className="mr-1" />
                {specialty.teacher_count}
              </Tag>
            )}
          </div>
          <div className="opacity-0 group-hover:opacity-100 transition-opacity">
            <Space size="small">
              <Tooltip title="Alt ixtisas əlavə et">
                <Button
                  type="text"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddChild(specialty);
                  }}
                />
              </Tooltip>
              <Tooltip title="Redaktə et">
                <Button
                  type="text"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEdit(specialty);
                  }}
                />
              </Tooltip>
              <Popconfirm
                title="İxtisası silmək"
                description={`${specialty.name} ixtisasını${specialty.children && specialty.children.length > 0 ? ' və bütün alt ixtisaslarını' : ''} silmək istədiyinizə əminsiniz?`}
                onConfirm={(e) => {
                  e?.stopPropagation();
                  handleDelete(specialty.id, specialty.name);
                }}
                okText="Bəli"
                cancelText="Xeyr"
                okType="danger"
              >
                <Tooltip title="Sil">
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={(e) => e.stopPropagation()}
                  />
                </Tooltip>
              </Popconfirm>
            </Space>
          </div>
        </div>
      ),
      key: specialty.id.toString(),
      icon: specialty.children && specialty.children.length > 0 ? <FolderOutlined /> : <FileOutlined />,
      children: specialty.children && Array.isArray(specialty.children) && specialty.children.length > 0 ? buildTreeData(specialty.children) : undefined,
      isLeaf: specialty.is_leaf,
      specialty: specialty
    }));
  };

  const handleAdd = () => {
    setEditingSpecialty(null);
    setParentSpecialty(null);
    setIsModalVisible(true);
  };

  const handleAddChild = (parent: SpecialtyTree) => {
    setEditingSpecialty(null);
    setParentSpecialty(parent);
    setIsModalVisible(true);
  };

  const handleEdit = (specialty: SpecialtyTree) => {
    setEditingSpecialty(specialty);
    setParentSpecialty(null);
    setIsModalVisible(true);
  };

  const handleDelete = async (id: number, _specialtyName: string) => {
    try {
      const response = await specialtyAPI.deleteSpecialty(id);
      if (response.success) {
        message.success(response.message || 'İxtisas silindi');
        loadSpecialties();
      } else {
        message.error(response.message || 'İxtisas silinmədi');
      }
    } catch (error) {
      console.error('İxtisas silinmədi:', error);
      message.error('İxtisas silinmədi');
    }
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setEditingSpecialty(null);
    setParentSpecialty(null);
  };

  const handleFormSuccess = () => {
    setIsModalVisible(false);
    setEditingSpecialty(null);
    setParentSpecialty(null);
    loadSpecialties();
  };

  const onExpand = (expandedKeysValue: React.Key[]) => {
    setExpandedKeys(expandedKeysValue as string[]);
    setAutoExpandParent(false);
  };

  const onSelect = (selectedKeysValue: React.Key[]) => {
    setSelectedKeys(selectedKeysValue as string[]);
  };

  const expandAll = () => {
    if (Array.isArray(specialties)) {
      const allKeys = getAllKeys(specialties);
      setExpandedKeys(allKeys);
    }
  };

  const collapseAll = () => {
    setExpandedKeys([]);
  };

  const getStatistics = () => {
    let totalSpecialties = 0;
    let activeSpecialties = 0;
    let totalTeachers = 0;

    const countSpecialties = (specialtyList: SpecialtyTree[]) => {
      if (!Array.isArray(specialtyList)) return;
      
      specialtyList.forEach(specialty => {
        totalSpecialties++;
        if (specialty.is_active) activeSpecialties++;
        totalTeachers += specialty.teacher_count;
        if (specialty.children && Array.isArray(specialty.children)) {
          countSpecialties(specialty.children);
        }
      });
    };

    if (Array.isArray(specialties)) {
      countSpecialties(specialties);
    }
    
    return { totalSpecialties, activeSpecialties, totalTeachers };
  };

  const { totalSpecialties, activeSpecialties, totalTeachers } = getStatistics();

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over || active.id === over.id) {
      return;
    }

    try {
      const draggedSpecialtyId = Number(active.id);
      const targetSpecialtyId = over.id === 'root' ? null : Number(over.id);

      const response = await specialtyAPI.moveSpecialty(draggedSpecialtyId, targetSpecialtyId || undefined);
      
      if (response.success) {
        message.success(response.message || 'İxtisas uğurla köçürüldü');
        loadSpecialties();
      } else {
        message.error(response.message || 'İxtisas köçürülmədi');
      }
    } catch (error) {
      console.error('Drag & Drop xətası:', error);
      message.error('İxtisas köçürülmədi');
    }
  };

  const renderDragDropSpecialties = (specialtyList: SpecialtyTree[], level: number = 0): React.ReactNode => {
    if (!Array.isArray(specialtyList)) return null;
    
    return specialtyList.map((specialty) => (
      <div key={specialty.id} style={{ marginLeft: level * 20 }}>
        <SortableTreeNode
          specialty={specialty}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onAddChild={handleAddChild}
        />
        {specialty.children && Array.isArray(specialty.children) && specialty.children.length > 0 && (
          <div className="ml-4">
            {renderDragDropSpecialties(specialty.children, level + 1)}
          </div>
        )}
      </div>
    ));
  };

  if (!selectedCompany) {
    return (
      <div className="text-center py-12">
        <BookOutlined className="text-6xl text-gray-300 mb-4" />
        <h3 className="text-xl text-gray-500 mb-2">Şirkət seçin</h3>
        <p className="text-gray-400">
          İxtisasları görmək üçün yuxarıdan şirkət seçin.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              <BookOutlined className="mr-3 text-green-500" />
              İxtisaslar
            </h2>
            <p className="text-gray-600 mt-1">
              {selectedCompany.name} şirkətinin ixtisas strukturu
            </p>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
            size="large"
            className="shadow-sm"
          >
            Yeni İxtisas
          </Button>
        </div>

        {/* Statistika kartları */}
        <Row gutter={[16, 16]} className="mb-6">
          <Col xs={24} sm={8}>
            <Card className="text-center shadow-sm">
              <div className="text-2xl font-bold text-green-600">{totalSpecialties}</div>
              <div className="text-gray-600">Ümumi İxtisas</div>
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card className="text-center shadow-sm">
              <div className="text-2xl font-bold text-blue-600">{activeSpecialties}</div>
              <div className="text-gray-600">Aktiv İxtisas</div>
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card className="text-center shadow-sm">
              <div className="text-2xl font-bold text-orange-600">{totalTeachers}</div>
              <div className="text-gray-600">Ümumi Müəllim</div>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Axtarış və əməliyyatlar */}
      <Card className="mb-6 shadow-sm">
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={8}>
            <Search
              placeholder="İxtisas axtar..."
              allowClear
              onSearch={(value) => setFilters({ ...filters, search: value })}
              className="w-full"
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Space>
              <Tooltip title="Hamısını aç">
                <Button
                  icon={<ExpandOutlined />}
                  onClick={expandAll}
                  disabled={dragMode}
                >
                  Aç
                </Button>
              </Tooltip>
              <Tooltip title="Hamısını bağla">
                <Button
                  icon={<CompressOutlined />}
                  onClick={collapseAll}
                  disabled={dragMode}
                >
                  Bağla
                </Button>
              </Tooltip>
            </Space>
          </Col>
          <Col xs={24} sm={24} md={8} className="text-right">
            <div className="flex items-center justify-end space-x-4">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">Drag & Drop:</span>
                <Switch
                  checked={dragMode}
                  onChange={setDragMode}
                  checkedChildren="Aktiv"
                  unCheckedChildren="Deaktiv"
                />
              </div>
            </div>
          </Col>
        </Row>
      </Card>

      {/* İxtisas ağacı */}
      <Card className="shadow-sm">
        <Spin spinning={loading}>
          {Array.isArray(specialties) && specialties.length > 0 ? (
            dragMode ? (
              // Drag & Drop Mode
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                <div className="space-y-2">
                  <Alert
                    message="Drag & Drop Rejimi"
                    description="İxtisasları sürüşdürüb başqa ixtisasın altına yerləşdirə bilərsiniz. İxtisasın solundakı ⋮⋮ ikonundan tutub sürüşdürün."
                    type="info"
                    showIcon
                    className="mb-4"
                  />
                  <SortableContext
                    items={specialties.map(c => c.id.toString())}
                    strategy={verticalListSortingStrategy}
                  >
                    {renderDragDropSpecialties(specialties)}
                  </SortableContext>
                </div>
                <DragOverlay>
                  {activeId ? (
                    <div className="p-3 bg-white border rounded-lg shadow-lg">
                      <div className="flex items-center space-x-2">
                        <BookOutlined className="text-blue-500" />
                        <span className="font-medium">İxtisas köçürülür...</span>
                      </div>
                    </div>
                  ) : null}
                </DragOverlay>
              </DndContext>
            ) : (
              // Tree Mode
              <DirectoryTree
                multiple={false}
                onExpand={onExpand}
                expandedKeys={expandedKeys}
                autoExpandParent={autoExpandParent}
                onSelect={onSelect}
                selectedKeys={selectedKeys}
                treeData={buildTreeData(specialties)}
                showIcon
                className="specialty-tree"
              />
            )
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <div>
                  <p>Hələ heç bir ixtisas yoxdur</p>
                  <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                    İlk ixtisası əlavə edin
                  </Button>
                </div>
              }
            />
          )}
        </Spin>
      </Card>

      {/* Modal */}
      {isModalVisible && (
        <SpecialtyForm
          visible={isModalVisible}
          specialty={editingSpecialty}
          parentSpecialty={parentSpecialty}
          onCancel={handleModalClose}
          onSuccess={handleFormSuccess}
          selectedCompany={selectedCompany}
        />
      )}
    </div>
  );
};

export default SpecialtiesList;